import { useLazyQuery } from '@apollo/client';
import {
  getLanguageDisplayName,
  getOrgLanguages,
  translateBackend,
} from '@assets/i18n/config';
import type { CustomField } from '@interfaces/customField';
import type { EnabledJurisdictions } from '@interfaces/organization';
import { Gender } from '@interfaces/user';
import { type CountryCodes, Jurisdictions } from '@onevesthq/ov-enums';
import { AddressField } from '@ovComponents/2-component/addressField/addressField';
import AlternativeAddressField from '@ovComponents/2-component/alternativeAddressField/alternativeAddressField';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { useHelpTextTokens } from '@ovComponents/2-component/helpText/helpText.tokens';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { PhoneNumberField } from '@ovComponents/3-pattern/phoneNumberField/phoneNumberField';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { isValidEmail, phoneValidator } from '@utils/common';
import dayjs from 'dayjs';
import { Alert, Box, Grid, Typography, useTheme } from 'hephaestus/web';
import type { CountryCode } from 'libphonenumber-js';
import { kebabCase, some } from 'lodash-es';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { buildEnrichedCustomOptions, invalidFields } from '../utils';
import { VALIDATE_AGE_OF_MAJORITY } from './personalInformation.queries';

export const PersonalInformationVisual = ({
  options,
  userData,
  loading,
  updateUser,
  continueFunc,
  grid = false,
  updateMode = false,
  activeCustomFields,
  derivedCustomFields,
  applicableJurisdictions,
  applicableCountries,
  workflowCompletion,
  ignoreCustomFieldTriggerRules,
  hasMailingAddress,
  hasAlternateAddress,
  setHasMailingAddress,
  setHasAlternateAddress,
}: {
  options: any;
  userData: any;
  loading: boolean;
  updateUser: any;
  continueFunc: any;
  grid?: boolean;
  updateMode?: boolean;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
  applicableJurisdictions?: EnabledJurisdictions;
  applicableCountries: CountryCodes[];
  workflowCompletion?: any;
  ignoreCustomFieldTriggerRules?: boolean;
  hasMailingAddress: boolean;
  hasAlternateAddress: boolean;
  setHasMailingAddress: (value: boolean) => void;
  setHasAlternateAddress: (value: boolean) => void;
}) => {
  const { sendAnalytic } = useContext(AnalyticsContext);
  const theme = useTheme();
  const tokens = useHelpTextTokens(theme);
  const { t } = useTranslation('client');
  const [focused, setFocused] = useState<string[]>([]);
  const [invalidFieldsList, setInvalidFieldsList] = useState<string[]>([]);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [invalidPhone, setInvalidPhone] = useState(false);
  const [ageOfMajority, setAgeOfMajority] = useState(0);
  const [isUnderAgeOfMajority, setIsUnderAgeOfMajority] = useState(false);
  const [isFutureDateOfBirth, setIsFutureDateOfBirth] = useState(false);
  const [updated, setUpdated] = useState<boolean>(false);
  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: userData.customFields,
    });
  const [validateAgeOfMajority] = useLazyQuery(VALIDATE_AGE_OF_MAJORITY);
  const [countryCode, setCountryCode] = useState<CountryCode | undefined>(
    undefined,
  );

  const shouldDisplayInDialog = tokens.displayInDialog;

  const submit = () => {
    const fields = invalidFields(
      {
        ...options,
        inProvinceSince: {
          ...options.inProvinceSince,
          requiredIf: (data: any) =>
            data.physicalAddress?.jurisdiction === 'CA_QC',
        },
        mailingAddress: {
          ...options.mailingAddress,
          requiredIf: (data: any) => data?.hasMailingAddress,
        },
        alternateAddress: {
          ...options.alternateAddress,
          requiredIf: (data: any) => data?.hasAlternateAddress,
        },
      },
      {
        ...userData,
        jurisdiction: userData.physicalAddress?.jurisdiction,
        hasMailingAddress,
        hasAlternateAddress,
      },
    );

    if (physicalAddressInvalid) fields.push('physicalAddress');
    if (mailingAddressInvalid) fields.push('mailingAddress');
    if (alternateAddressInvalid) fields.push('alternateAddress');

    if (userData.primaryEmail && !isValidEmail(userData.primaryEmail)) {
      fields.push('primaryEmail');
      setInvalidEmail(true);
    } else {
      setInvalidEmail(false);
    }

    if (
      userData.phone &&
      !phoneValidator(userData.phone, countryCode, applicableCountries) &&
      options.phone.enabled
    ) {
      fields.push('phone');
      setInvalidPhone(true);
    } else {
      setInvalidPhone(false);
    }

    const invalidCustomFieldKeys = validateAllCustomFields();
    fields.push(...invalidCustomFieldKeys);

    setFocused(fields);
    setInvalidFieldsList(fields);

    if (fields.length === 0 && !isUnderAgeOfMajority && !isFutureDateOfBirth) {
      sendAnalytic(
        ovAnalyticsEvents.workflowsPersonalInformationContinueButtonSelect,
        {
          workflowStepTitle: options?.title,
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );
      continueFunc();
      setUpdated(false);
    } else if (isUnderAgeOfMajority || isFutureDateOfBirth) {
      update({ ...userData, dateOfBirth: null });
    }
  };

  const update = (newValue: any) => {
    setUpdated(true);
    updateUser(newValue);
  };

  useEffect(() => {
    setCustomOptionSelected(
      buildEnrichedCustomOptions(
        options,
        activeCustomFields,
        derivedCustomFields,
      ),
    );
  }, [options, activeCustomFields, derivedCustomFields]);

  const error = (key: string): boolean =>
    ((!userData?.[key] && focused.includes(key)) ||
      invalidFieldsList.includes(key)) &&
    options?.[key]?.required !== FieldRequiredType.NOT_REQUIRED;

  const allJurisdictionsOfApplicableCountries = useMemo(
    () =>
      Object.keys(Jurisdictions).filter((j) =>
        some(applicableCountries, (c: any) => j.startsWith(c)),
      ),
    [applicableCountries],
  );

  const getJurisdictionOptionLabel = (code: any): string => {
    const alsoShowCountry = applicableCountries.length > 1;
    return (
      (alsoShowCountry && code ? `${code.substring(0, 2)} - ` : '') +
      t(`geoNames:${code}`)
    );
  };

  const getAddressOutOfJurisdiction = ({
    addressOptions,
    applicableJurisdictionsToCheck,
    addressUserData,
  }: {
    addressOptions: any;
    applicableJurisdictionsToCheck?: EnabledJurisdictions;
    addressUserData: any;
  }) => {
    if (!addressOptions?.enabled && !addressOptions?.jurisdiction?.enabled)
      return false;
    if (!addressUserData) return false;
    if (!addressUserData?.jurisdiction) return false;

    if (!applicableJurisdictionsToCheck) return false;

    if (applicableJurisdictionsToCheck.all === true) return false;
    if (
      !applicableJurisdictionsToCheck.only ||
      applicableJurisdictionsToCheck.only.length === 0
    )
      return false;

    if (
      applicableJurisdictionsToCheck.only.includes(
        addressUserData?.jurisdiction,
      )
    )
      return false;

    const errMessage =
      translateBackend(addressOptions?.addressOutOfJurisdictionMessage) ||
      t('client:profile.fieldValidations.addressOutOfJurisdictionMessage');
    return errMessage.replace(
      '{{ALLOWED_JURISDICTIONS}}',
      (applicableJurisdictionsToCheck?.only ?? [])
        ?.map((j: any) => j.replace(/^(CA|US)_/, ''))
        .join(', '),
    );
  };

  const physicalAddressOutOfJurisdiction: false | string = useMemo(
    () =>
      getAddressOutOfJurisdiction({
        addressOptions: options.physicalAddress,
        applicableJurisdictionsToCheck: applicableJurisdictions,
        addressUserData: userData.physicalAddress,
      }),
    [options.physicalAddress, userData, applicableJurisdictions],
  );

  const mailingAddressOutOfJurisdiction: false | string = useMemo(
    () =>
      getAddressOutOfJurisdiction({
        addressOptions: options.mailingAddress,
        applicableJurisdictionsToCheck: applicableJurisdictions,
        addressUserData: userData.mailingAddress,
      }),
    [options.mailingAddress, userData, applicableJurisdictions],
  );

  const physicalAddressInvalid =
    physicalAddressOutOfJurisdiction !== false &&
    options?.physicalAddress?.required === FieldRequiredType.ERROR;
  const mailingAddressInvalid =
    mailingAddressOutOfJurisdiction !== false &&
    options?.mailingAddress?.required === 'ERROR' &&
    hasMailingAddress;
  const alternateAddressInvalid =
    (!userData?.alternateAddress?.country ||
      !userData?.alternateAddress?.line1) &&
    options?.alternateAddress?.required === 'ERROR' &&
    hasAlternateAddress;
  const formDisabled =
    loading ||
    (!updated && updateMode) ||
    physicalAddressInvalid ||
    mailingAddressInvalid;

  useEffect(() => {
    if (!options?.dateOfBirth?.enabled) return;
    if (!focused.includes('dateOfBirth')) return;
    if (!userData.dateOfBirth || !userData.physicalAddress?.jurisdiction)
      return;

    const isDateInFuture =
      userData.dateOfBirth && dayjs(userData.dateOfBirth).isAfter(dayjs());
    setIsFutureDateOfBirth(isDateInFuture);
    if (isDateInFuture) {
      update({ ...userData, dateOfBirth: null });
      return;
    }

    validateAgeOfMajority({
      variables: {
        dateOfBirth: userData.dateOfBirth,
        country: userData.physicalAddress?.country,
        jurisdiction: userData.physicalAddress?.jurisdiction,
      },
      onCompleted: (data) => {
        setIsUnderAgeOfMajority(!data.validateAgeOfMajority.valid);
        if (
          !data.validateAgeOfMajority.valid &&
          dayjs(userData.dateOfBirth).isValid()
        ) {
          update({ ...userData, dateOfBirth: null });
        }
        setAgeOfMajority(data.validateAgeOfMajority.ageOfMajority);
      },
    });
  }, [userData, options, focused]);

  const updateJurisdiction = (_e: any, newValue: string) => {
    const physicalAddress = newValue
      ? {
          ...userData.physicalAddress,
          jurisdiction: newValue,
          country: newValue?.substring(0, 2),
        }
      : undefined;
    update({ ...userData, physicalAddress });
  };

  useEffect(() => {
    setInvalidFieldsList((prev) => {
      const without = prev.filter((key) => key !== 'physicalAddress');
      if (physicalAddressOutOfJurisdiction !== false)
        without.push('physicalAddress');
      if (mailingAddressOutOfJurisdiction !== false)
        without.push('mailingAddress');
      return without;
    });
  }, [physicalAddressOutOfJurisdiction, mailingAddressOutOfJurisdiction]);

  const underAgeOfMajorityMessage = options?.dateOfBirth
    ?.underAgeOfMajorityMessage
    ? translateBackend(options.dateOfBirth.underAgeOfMajorityMessage)
    : t('workflowCompletions:personalInformation.underAgeOfMajorityMessage');

  const futureDateMessage = options?.dateOfBirth?.futureDateMessage
    ? translateBackend(options.dateOfBirth.futureDateMessage)
    : t('workflowCompletions:personalInformation.futureDateMessage');

  const lockMessage = () =>
    options.customLockedMessage
      ? translateBackend(options.customLockedMessage)
      : t('pageConfiguration:notEditableMessage');

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='personal-information-form'
      showSubmitButton={!updateMode || options.displayUpdateButton}
      submitButtonProps={{
        label: t(updateMode ? 'update' : 'continue'),
        disabled: formDisabled,
        dataTestId: `personal-information-${kebabCase(translateBackend(options?.customTitle || options?.title))}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
          {translateBackend(options?.title)}
          <Box
            sx={{
              display: 'inline-flex',
            }}
          >
            {additionalInfo(options?.title?.additionalInfo) && (
              <InfoDialog
                information={
                  additionalInfo(options?.title?.additionalInfo) ?? ''
                }
              />
            )}
          </Box>
        </Typography>
      )}
      {options?.subtitle && (
        <Typography
          variant='bodyLarge'
          component={'div'}
          sx={{
            mb: 3,
            table: { width: '100%' },
            display: 'flex',
            alignItems: 'end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              m: 0,
              '& > *': { m: 0 },
            }}
          >
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(options?.subtitle)}
            </ReactMarkdown>
          </Box>
          {additionalInfo(options?.subtitle?.additionalInfo) && (
            <InfoDialog
              information={
                additionalInfo(options?.subtitle?.additionalInfo) ?? ''
              }
            />
          )}
        </Typography>
      )}

      <Grid container spacing={2}>
        {options?.firstName?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='legal-first-name'
              onChange={(e: any) =>
                update({ ...userData, firstName: e.target.value })
              }
              disabled={
                loading ||
                options?.firstName?.required === FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.firstName?.label)}
              fullWidth
              value={userData.firstName ?? ''}
              onBlur={() => setFocused([...focused, 'firstName'])}
              error={error('firstName')}
              locked={
                options?.firstName?.required === FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              infoTooltip={additionalInfo(options?.firstName?.additionalInfo)}
            />
          </Grid>
        )}
        {options?.middleName?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='legal-middle-name'
              onChange={(e: any) =>
                update({ ...userData, middleName: e.target.value })
              }
              disabled={
                loading ||
                options?.middleName?.required === FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.middleName?.label)}
              fullWidth
              value={userData.middleName ?? ''}
              onBlur={() => setFocused([...focused, 'middleName'])}
              error={error('middleName')}
              locked={
                options?.middleName?.required === FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              infoTooltip={additionalInfo(options?.middleName?.additionalInfo)}
            />
          </Grid>
        )}
        {options?.lastName?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='legal-last-name'
              onChange={(e: any) =>
                update({ ...userData, lastName: e.target.value })
              }
              disabled={
                loading ||
                options?.lastName?.required === FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.lastName?.label)}
              fullWidth
              value={userData.lastName ?? ''}
              onBlur={() => setFocused([...focused, 'lastName'])}
              error={error('lastName')}
              locked={
                options?.lastName?.required === FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              infoTooltip={additionalInfo(options?.lastName?.additionalInfo)}
            />
          </Grid>
        )}
        {options?.jurisdiction?.enabled && (
          <Grid size={12}>
            <Autocomplete
              testId='jurisdiction-field'
              label={translateBackend(options?.jurisdiction?.label)}
              fullWidth
              options={allJurisdictionsOfApplicableCountries}
              getOptionLabel={getJurisdictionOptionLabel}
              value={userData.physicalAddress?.jurisdiction ?? ''}
              onChange={updateJurisdiction}
              error={
                !userData.physicalAddress?.jurisdiction &&
                focused.includes('jurisdiction') &&
                options.jurisdiction.required !== FieldRequiredType.NOT_REQUIRED
              }
              locked={
                options?.jurisdiction?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              infoTooltip={additionalInfo(
                options?.jurisdiction?.additionalInfo,
              )}
              onBlur={() => setFocused([...focused, 'jurisdiction'])}
            />
          </Grid>
        )}
        {options?.physicalAddress?.enabled && (
          <Grid size={12}>
            <AddressField
              testId='physical-address'
              address={userData.physicalAddress}
              onChange={(e: any) => update({ ...userData, physicalAddress: e })}
              disabled={
                loading ||
                options?.physicalAddress?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.physicalAddress?.label)}
              fullWidth
              lockMessage={lockMessage()}
              defaultAddressProvider={
                options?.physicalAddress?.defaultAddressProvider
              }
              manualAddressEntry={options?.physicalAddress?.manualAddressEntry}
              infoTooltip={additionalInfo(
                options?.physicalAddress?.additionalInfo,
              )}
              onFocus={() => setFocused([...focused, 'physicalAddress'])}
              error={error('physicalAddress')}
            />
          </Grid>
        )}
        {options?.mailingAddress?.enabled && (
          <Grid size={12}>
            <RadioGroup
              label={t('components:address.mailAddressCheck')}
              value={hasMailingAddress === true ? 'yes' : 'no'}
              onChange={(e: any) => {
                setHasMailingAddress(e.target.value === 'yes');
                update({
                  ...userData,
                  mailingAddress:
                    e.target.value === 'yes'
                      ? userData.mailingAddress
                      : undefined,
                });
              }}
              disabled={
                loading ||
                options?.mailingAddress?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
            >
              <Radio
                value={'no'}
                label={t('components:booleanOptions.no')}
                disabled={
                  loading ||
                  options?.mailingAddress?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
              />
              <Radio
                value={'yes'}
                label={t('components:booleanOptions.yes')}
                disabled={
                  loading ||
                  options?.mailingAddress?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
              />
            </RadioGroup>
          </Grid>
        )}
        {options?.mailingAddress?.enabled && hasMailingAddress && (
          <Grid size={12}>
            <AddressField
              testId='mailing-address'
              address={userData.mailingAddress}
              onChange={(e: any) => update({ ...userData, mailingAddress: e })}
              disabled={
                loading || options?.mailingAddress?.required === 'NOT_EDITABLE'
              }
              label={translateBackend(options?.mailingAddress?.label)}
              fullWidth
              lockMessage={lockMessage()}
              defaultAddressProvider={
                options?.physicalAddress?.defaultAddressProvider
              }
              manualAddressEntry={options?.physicalAddress?.manualAddressEntry}
              infoTooltip={additionalInfo(
                options?.mailingAddress?.additionalInfo,
              )}
              onFocus={() => setFocused([...focused, 'mailingAddress'])}
              error={error('mailingAddress')}
            />
          </Grid>
        )}
        {(physicalAddressOutOfJurisdiction !== false ||
          (mailingAddressOutOfJurisdiction !== false && hasMailingAddress)) && (
          <Grid size={12}>
            <Alert
              severity={
                options?.physicalAddress?.required === 'ERROR'
                  ? 'error'
                  : 'info'
              }
            >
              {physicalAddressOutOfJurisdiction ||
                mailingAddressOutOfJurisdiction}
            </Alert>
          </Grid>
        )}
        {options?.alternateAddress?.enabled && (
          <Grid size={12}>
            <RadioGroup
              label={t('components:address.alternateAddressCheck')}
              value={hasAlternateAddress === true ? 'yes' : 'no'}
              onChange={(e: any) => {
                setHasAlternateAddress(e.target.value === 'yes');
                update({
                  ...userData,
                  alternateAddress:
                    e.target.value === 'yes'
                      ? userData.alternateAddress
                      : undefined,
                });
              }}
              disabled={
                loading ||
                options?.alternateAddress?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
            >
              <Radio
                value={'no'}
                label={t('components:booleanOptions.no')}
                disabled={
                  loading ||
                  options?.alternateAddress?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
              />
              <Radio
                value={'yes'}
                label={t('components:booleanOptions.yes')}
                disabled={
                  loading ||
                  options?.alternateAddress?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
              />
            </RadioGroup>
          </Grid>
        )}
        {options?.alternateAddress?.enabled && hasAlternateAddress && (
          <Grid size={12}>
            <AlternativeAddressField
              testId='alternate-address'
              address={userData.alternateAddress}
              onChange={(e: any) =>
                update({ ...userData, alternateAddress: e })
              }
              disabled={
                loading ||
                options?.alternateAddress?.required === 'NOT_EDITABLE'
              }
              locked={options?.alternateAddress?.required === 'NOT_EDITABLE'}
              lockMessage={lockMessage()}
              label={translateBackend(options?.alternateAddress?.label)}
              infoTooltip={additionalInfo(
                options?.alternateAddress?.additionalInfo,
              )}
              error={error('alternateAddress')}
            />
          </Grid>
        )}
        {options?.inProvinceSince?.enabled &&
          userData.physicalAddress?.jurisdiction === 'CA_QC' && (
            <Grid size={{ xs: 12, md: grid ? 6 : 12 }}>
              <DateField
                testId='in-province-since'
                onChange={(date: any) =>
                  update({
                    ...userData,
                    inProvinceSince: dayjs(date?.toString()).format(
                      'YYYY-MM-DD',
                    ),
                  })
                }
                disabled={
                  loading ||
                  options?.inProvinceSince?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                label={translateBackend(options?.inProvinceSince?.label)}
                lockMessage={lockMessage()}
                fullWidth
                value={userData.inProvinceSince}
                onBlur={() => setFocused([...focused, 'inProvinceSince'])}
                infoTooltip={additionalInfo(
                  options?.inProvinceSince?.additionalInfo,
                )}
                error={error('inProvinceSince')}
              />
            </Grid>
          )}
        {options?.dateOfBirth?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 6 : 12 }}>
            <DateField
              dataTestId='date-of-birth'
              onChange={(date: any) =>
                update({
                  ...userData,
                  dateOfBirth: dayjs(date?.toString()).format('YYYY-MM-DD'),
                })
              }
              disabled={
                loading ||
                options?.dateOfBirth?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.dateOfBirth?.label)}
              lockMessage={lockMessage()}
              fullWidth
              value={userData.dateOfBirth}
              onBlur={() => setFocused([...focused, 'dateOfBirth'])}
              infoTooltip={additionalInfo(options?.dateOfBirth?.additionalInfo)}
              error={
                error('dateOfBirth') ||
                isUnderAgeOfMajority ||
                isFutureDateOfBirth
              }
            />
            {isUnderAgeOfMajority && (
              <Alert
                severity={
                  options?.dateOfBirth?.required === FieldRequiredType.ERROR
                    ? 'error'
                    : 'info'
                }
                sx={{ my: 2 }}
              >
                {underAgeOfMajorityMessage.replace(
                  '{{MINUMUM_AGE}}',
                  ageOfMajority.toString(),
                )}
              </Alert>
            )}
            {isFutureDateOfBirth && (
              <Alert severity='error'>{futureDateMessage}</Alert>
            )}
          </Grid>
        )}
        {options?.primaryEmail?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 6 : 12 }}>
            <TextField
              testId='primary-email'
              onChange={(e: any) =>
                update({ ...userData, primaryEmail: e.target.value })
              }
              disabled={
                loading ||
                options?.primaryEmail?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.primaryEmail?.label)}
              fullWidth
              value={userData.primaryEmail ?? ''}
              onBlur={() => setFocused([...focused, 'primaryEmail'])}
              error={error('primaryEmail')}
              errorText={
                invalidEmail ? t('components:fieldErrors.email') : undefined
              }
              locked={
                options?.primaryEmail?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              infoTooltip={additionalInfo(
                options?.primaryEmail?.additionalInfo,
              )}
            />
          </Grid>
        )}
        {options?.phone?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 6 : 12 }}>
            <PhoneNumberField
              testId='phone'
              onChange={(e: any) => {
                update({ ...userData, phone: e.value });
                setCountryCode(e.countryCode);
              }}
              disabled={
                loading ||
                options?.phone?.required === FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.phone?.label)}
              fullWidth
              value={userData.phone ?? ''}
              onBlur={() => setFocused([...focused, 'phone'])}
              error={error('phone') || invalidPhone}
              errorText={
                invalidPhone ? t('components:fieldErrors.phone') : undefined
              }
              locked={
                options?.phone?.required === FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              infoTooltip={additionalInfo(options?.phone?.additionalInfo)}
            />
          </Grid>
        )}
        {options?.preferredMethodOfCommunication?.enabled && (
          <Grid size={12}>
            <RadioGroup
              testId='preferred-method-of-communication'
              onChange={(e: any) => {
                update({
                  ...userData,
                  preferredMethodOfCommunication: e.target.value,
                });
                setFocused([...focused, 'preferredMethodOfCommunication']);
              }}
              disabled={
                loading ||
                options?.preferredMethodOfCommunication?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(
                options?.preferredMethodOfCommunication?.label,
              )}
              infoTooltip={additionalInfo(
                options?.preferredMethodOfCommunication?.additionalInfo,
              )}
              fullWidth
              value={userData.preferredMethodOfCommunication}
              onFocus={() =>
                setFocused([...focused, 'preferredMethodOfCommunication'])
              }
              error={error('preferredMethodOfCommunication')}
            >
              {['EMAIL', 'PHONE', 'TEXT'].map((comm) => (
                <Radio
                  testId={`preferred-method-of-communication-${comm.toLowerCase()}`}
                  value={comm}
                  disabled={
                    loading ||
                    options?.preferredMethodOfCommunication?.required ===
                      FieldRequiredType.NOT_EDITABLE
                  }
                  label={t(
                    `edit.preferredMethodOfCommunicationOptions.${comm}`,
                  )}
                />
              ))}
            </RadioGroup>
          </Grid>
        )}
        {options?.gender?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 6 : 12 }}>
            <RadioGroup
              testId='gender-select'
              onChange={(e: any) => {
                update({ ...userData, gender: e.target.value });
                setFocused([...focused, 'gender']);
              }}
              disabled={
                loading ||
                options?.gender?.required === FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.gender?.label)}
              infoTooltip={additionalInfo(options?.gender?.additionalInfo)}
              fullWidth
              value={userData.gender}
              error={error('gender')}
            >
              {Object.values(Gender).map((gender) => (
                <Radio
                  testId={`gender-select-${gender.toLowerCase()}`}
                  value={gender}
                  disabled={
                    loading ||
                    options?.gender?.required === FieldRequiredType.NOT_EDITABLE
                  }
                  label={t(`shared:genderOptions.${gender}`)}
                />
              ))}
            </RadioGroup>
          </Grid>
        )}
        {options?.language?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 6 : 12 }}>
            <RadioGroup
              testId='language-select'
              onChange={(e: any) => {
                update({ ...userData, language: e.target.value });
                setFocused([...focused, 'language']);
              }}
              disabled={
                loading ||
                options?.language?.required === FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.language?.label)}
              infoTooltip={additionalInfo(options?.language?.additionalInfo)}
              fullWidth
              value={userData.language}
              onFocus={() => setFocused([...focused, 'language'])}
              error={error('language')}
            >
              {getOrgLanguages().map((language) => (
                <Radio
                  key={language}
                  testId={`language-select-${language.toLowerCase()}`}
                  value={language}
                  disabled={
                    loading ||
                    options?.language?.required ===
                      FieldRequiredType.NOT_EDITABLE
                  }
                  label={getLanguageDisplayName(language)}
                />
              ))}
            </RadioGroup>
          </Grid>
        )}
        <Grid size={12}>
          {renderCustomFields({
            customData: userData,
            update,
            grid,
            focused,
            setFocused,
            loading,
            ignoreCustomFieldTriggerRules,
            userId: userData.id,
          })}
        </Grid>
      </Grid>

      {!!translateBackend(options?.termsAndConditionsText)?.trim() && (
        <Box sx={{ mb: 3, mt: 3 }}>
          <Typography
            data-testid='terms-and-condition'
            variant='bodyLarge'
            component='div'
            sx={{
              table: { width: '100%' },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} linkTarget={'_blank'}>
              {translateBackend(options?.termsAndConditionsText)}
            </ReactMarkdown>
            {additionalInfo(options?.termsAndConditionsText?.additionalInfo) &&
              shouldDisplayInDialog && (
                <InfoDialog
                  information={
                    additionalInfo(
                      options?.termsAndConditionsText?.additionalInfo,
                    ) ?? ''
                  }
                />
              )}
          </Typography>
          {additionalInfo(options?.termsAndConditionsText?.additionalInfo) &&
            !shouldDisplayInDialog && (
              <HelpText
                text={additionalInfo(
                  options?.termsAndConditionsText?.additionalInfo,
                )}
                tone='neutral'
              />
            )}
        </Box>
      )}
    </WorkflowCompletionForm>
  );
};

export default PersonalInformationVisual;
